import { Component, OnInit } from "@angular/core";
import { SignaturesService, wyswigConfig } from "../signatures.service";
import { AngularEditorConfig } from "@kolkov/angular-editor";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
  ValidatorFn,
} from "@angular/forms";
import { SaveTemplatePredefined } from "../signatures.interface";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import { debounceTime } from "rxjs/operators";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";

@Component({
  selector: "app-edit-signature-predefined",
  templateUrl: "./edit-signature-predefined.component.html",
  styleUrls: ["./edit-signature-predefined.component.scss"],
  standalone: false,
})
export class EditSignaturePredefinedComponent implements OnInit {
  public readonly config: AngularEditorConfig = wyswigConfig;
  public readonly editExisting: boolean;

  public saving = false;
  public errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  public form: FormGroup;

  constructor(
    private readonly service: SignaturesService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrTranslatedService,
  ) {
    const params = this.route.snapshot.params as {
      templatePredefinedId: string;
    };
    this.editExisting = Boolean(params.templatePredefinedId);
    const requiredValidator: ValidatorFn = Validators.required.bind(
      null,
    ) as ValidatorFn;
    this.form = new FormGroup({
      name: new FormControl(""),
      content: new FormControl("", requiredValidator),
      placeholderVariables: new FormArray([]),
    });
  }

  ngOnInit(): void {
    if (this.editExisting) {
      const params = this.route.snapshot.params as {
        templatePredefinedId: string;
      };
      this.service
        .predefinedById(params.templatePredefinedId)
        .subscribe((f) => {
          this.form.patchValue({
            name: f.name,
            content: f.content,
          });
          this.updatePlaceholderVariables(f.placeholderVariables ?? {});
        });
    }

    this.form.controls.content.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value) => {
        const variableNames = this.findAllPlaceholderVariablesInHtmlContent(
          value as string,
        );
        this.synchronizePlaceholderVariablesWithHtmlVariables(variableNames);
      });
  }

  public get placeholderVariables(): FormArray {
    return this.form.get("placeholderVariables") as FormArray;
  }

  public save() {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid || this.saving) {
      return;
    }
    this.saving = true;
    const body = this.normalizeFormToRequestBody();

    const params = this.route.snapshot.params as {
      templatePredefinedId: string;
    };
    const request = this.editExisting
      ? this.service.updatePredefined(params.templatePredefinedId, body)
      : this.service.addPredefined(body);

    void request
      .toPromise()
      .then(() => this.toastr.success("footers.updated"))
      .then(() => this.goToSignatures())
      .finally(() => (this.saving = false));
  }

  public goToSignatures() {
    void this.router.navigate(["signatures", "library"]);
  }

  public removePlaceholderVariable(item: AbstractControl) {
    this.placeholderVariables.removeAt(
      this.placeholderVariables.controls.indexOf(item),
    );
  }

  private normalizeFormToRequestBody(): SaveTemplatePredefined {
    const placeholderVariables = (
      this.placeholderVariables.getRawValue() as {
        name: string;
        value: string;
      }[]
    ).reduce(
      (obj, item) => {
        obj[item.name] = item.value;
        return obj;
      },
      {} as Record<string, string>,
    );

    return {
      ...(this.form.getRawValue() as Omit<
        SaveTemplatePredefined,
        "placeholderVariables"
      >),
      placeholderVariables,
    };
  }

  private findAllPlaceholderVariablesInHtmlContent(htmlContent: string) {
    const regex = /\$\{.*?}/g;
    const variableNames =
      htmlContent?.match(regex)?.map((match) => match.slice(2, -1)) ?? [];

    return [...new Set(variableNames)];
  }

  private synchronizePlaceholderVariablesWithHtmlVariables(
    variableNames: string[],
  ) {
    const existingVariableNames = this.placeholderVariables.controls.map(
      (control) => (control.value as { name: string }).name,
    );
    const newVariableNames = variableNames.filter(
      (name) => !existingVariableNames.includes(name),
    );
    const removedVariableNames = existingVariableNames.filter(
      (name) => !variableNames.includes(name),
    );

    const requiredValidator: ValidatorFn = Validators.required.bind(
      null,
    ) as ValidatorFn;
    newVariableNames.forEach((name) =>
      this.placeholderVariables.push(
        new FormGroup({
          name: new FormControl(name, requiredValidator),
          value: new FormControl("", requiredValidator),
          existsInContent: new FormControl(true),
        }),
      ),
    );

    existingVariableNames.forEach((name) => {
      const index = this.placeholderVariables.controls.findIndex(
        (control) => (control.value as { name: string }).name === name,
      );
      this.placeholderVariables.at(index).get("existsInContent").setValue(true);
    });

    removedVariableNames.forEach((name) => {
      const index = this.placeholderVariables.controls.findIndex(
        (control) => (control.value as { name: string }).name === name,
      );
      this.placeholderVariables
        .at(index)
        .get("existsInContent")
        .setValue(false);
    });
  }

  private updatePlaceholderVariables(
    placeholderVariables: Record<string, string>,
  ) {
    const requiredValidator: ValidatorFn = Validators.required.bind(
      null,
    ) as ValidatorFn;
    Object.entries(placeholderVariables).forEach(([name, value]) => {
      this.placeholderVariables.push(
        new FormGroup({
          name: new FormControl(name, requiredValidator),
          value: new FormControl(value, requiredValidator),
          existsInContent: new FormControl(true),
        }),
      );
    });
  }
}
